import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Printer } from "lucide-react";
import { api, fmtDateTime, openLabels } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { boxLabelOf } from "@/pages/BoxContents";

export default function Boxes() {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState([]);
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/boxes").then((r) => setBoxes(r.data)).catch(() => {});
  }, []);
  const shown = boxes.filter((b) => `${b.box_number} ${b.location || ""}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (c) => setSel((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));
  const print = () => openLabels(boxes.filter((b) => sel.includes(b.barcode)).map(boxLabelOf), navigate);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="boxes-page">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Boxes</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-6">Boxes & box labels</h2>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Box number or location" className="h-10 w-64" data-testid="boxes-search-input" />
        <Button variant="ghost" onClick={() => setSel(sel.length === shown.length ? [] : shown.map((b) => b.barcode))} disabled={!shown.length} data-testid="boxes-select-all">{sel.length && sel.length === shown.length ? "Select none" : "Select all"}</Button>
        <Button onClick={print} disabled={!sel.length} className="bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="boxes-print-button"><Printer className="w-4 h-4 mr-2" />Print {sel.length} box label{sel.length === 1 ? "" : "s"}</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((b) => (
          <div key={b.id} className={`rounded-xl border bg-[#161214] p-4 flex gap-3 transition-colors ${sel.includes(b.barcode) ? "border-[#D4AF37]" : "border-[#2E252A]"}`} data-testid={`box-card-${b.barcode}`}>
            <input type="checkbox" className="mt-1.5 accent-[#D4AF37]" checked={sel.includes(b.barcode)} onChange={() => toggle(b.barcode)} data-testid={`box-select-${b.barcode}`} />
            <button onClick={() => navigate(`/b/${encodeURIComponent(b.barcode)}`)} className="text-left flex-1 min-w-0" data-testid={`box-open-${b.barcode}`}>
              <p className="font-serif text-xl text-[#F5F0EB] hover:text-[#D4AF37]"><Package className="w-4 h-4 inline mr-2 text-[#D4AF37]" />Box {b.box_number}</p>
              <p className="text-sm text-[#A89F91] truncate">{b.location || "Unassigned"} · {b.bottle_count} btl</p>
              <p className="text-xs text-[#6E665D] mt-1">Last change {fmtDateTime(b.last_change)}</p>
            </button>
          </div>
        ))}
        {!shown.length && <p className="text-[#6E665D] col-span-full py-10 text-center">No boxes yet — boxes are created when you add bottles with a box number.</p>}
      </div>
    </div>
  );
}
